import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./SignIn.module.sass";
import { use100vh } from "react-div-100vh";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import TextInput from "../../components/TextInput";
import PasswordInput from "../../components/PasswordInput";
import Image from "../../components/Image";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";

const SignIn = () => {
  const heightWindow = use100vh();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Capturer le sessionId depuis l'URL pour les signatures
  useEffect(() => {
    const attendanceId = searchParams.get('attendanceId');
    if (attendanceId) {
      sessionStorage.setItem('pendingSignatureAttendanceId', attendanceId);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        // Vérifier s'il y a une redirection vers une page de signature en attente
        const redirectAfterLogin = sessionStorage.getItem('redirectAfterLogin');
        const pendingSignatureAttendanceId = sessionStorage.getItem('pendingSignatureAttendanceId');
        
        if (redirectAfterLogin) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectAfterLogin);
          toast.success("Connexion réussie ! Vous pouvez maintenant signer.");
          return;
        }
        
        if (pendingSignatureAttendanceId) {
          sessionStorage.removeItem('pendingSignatureAttendanceId');
          navigate(`/signature/${pendingSignatureAttendanceId}`);
          toast.success("Connexion réussie ! Vous pouvez maintenant signer.");
          return;
        }

        // Vérifier si c'est la première connexion
        if (result.user.first_connexion === true) {
          // Redirection vers la page de complétion de compte en fonction du rôle
          if (result.user.role.includes("Student")) {
            navigate("/complete-account");
            toast.success("Bienvenue sur WakeUpSuccess ! Veuillez compléter votre profil étudiant");
          } else if (result.user.role.includes("Company")) {
            navigate("/account/complete-account");
            toast.success("Bienvenue sur WakeUpSuccess ! Veuillez compléter votre profil entreprise");
          } else if (result.user.role.includes("Admin")) {
            navigate("/partner/complete-account");
            toast.success("Bienvenue sur WakeUpSuccess ! Veuillez compléter votre profil partenaire");
          } else {
            navigate("/complete-account");
            toast.success("Bienvenue sur WakeUpSuccess ! Veuillez compléter votre profil");
          }
          return;
        }
        
        // Vérifier le rôle de l'utilisateur et rediriger en conséquence
        if (result.user.role.includes("SuperAdmin")) {
          navigate("/dashboard"); // Redirection vers le dashboard SuperAdmin
          toast.success("Connexion réussie en tant que Super Administrateur");
        } else if (result.user.role.includes("Admin")) {
          navigate("/admin/dashboard"); // Redirection vers le dashboard admin
          toast.success("Connexion réussie en tant qu'administrateur");
        } else if (result.user.role.includes("Commercial")) {
          navigate("/commercial/prospects_student"); // Redirection vers les prospects étudiants
          toast.success("Connexion réussie en tant que commercial");
        } else if (result.user.role.includes("Pedagogical")) {
          navigate("/pedagogical/dashboard"); // Redirection vers le dashboard pédagogique
          toast.success("Connexion réussie en tant que responsable pédagogique");
        } else if (result.user.role.includes("Teacher")) {
          navigate("/teacher/sessions-formation"); // Redirection vers les sessions de formation
          toast.success("Connexion réussie en tant qu'enseignant");
        } else if (result.user.role.includes("Student")) {
          navigate("/student/dashboard"); // Redirection vers la page compte étudiant
          toast.success("Connexion réussie en tant qu'étudiant");
        } else if (result.user.role.includes("Company")) {
          navigate("/company/dashboard"); // Redirection vers la page compte entreprise
          toast.success("Connexion réussie en tant qu'entreprise");
        } else {
          navigate("/dashboard"); // Redirection par défaut
          toast.success("Connexion réussie");
        }
      } else if (result.inactive) {
        // Rediriger vers la page de compte inactif
        navigate("/account-inactive");
      } else {
        toast.error("Échec de la connexion");
      }
    } catch (error) {
      console.error("Erreur lors de la connexion:", error);
      toast.error("Une erreur est survenue lors de la connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.row}>
      <div 
        className={styles.col}
        style={{
          backgroundImage: `url('/images/content/loginbg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
          flex: 1
        }}
      />
      <div className={styles.col} style={{ minHeight: heightWindow, flex: 1.5}}>
        <div className={styles.head}>
          <Link className={styles.logo} to="/">
            <Image
              className={styles.pic}
              src="/images/logo.png"
              srcDark="/images/logo.png"
              alt="Core"
            />
          </Link>
          <div className={styles.info}>
            Mot de passe oublié ?{" "}
            <Link className={styles.link} to="/forgot-password">
              Réinitialiser
            </Link>
          </div>
        </div>
        <div className={styles.wrapper}>
          <div className={cn("h2", styles.title)}>Se connecter</div>
          <div className={styles.separator}>
            <div className={styles.line}></div>
          </div>
          <div className={styles.body}>
            <div className={styles.subtitle}>Veuillez saisir vos identifiants</div>
            <form onSubmit={handleSubmit}>
              <TextInput
                className={styles.field}
                name="email"
                type="email"
                placeholder="Votre email"
                required
                icon="mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <PasswordInput
                className={styles.field}
                name="password"
                placeholder="Mot de passe"
                required
                icon="lock"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button 
                className={cn("button", styles.button)} 
                type="submit"
                disabled={loading}
              >
                {loading ? "Connexion en cours..." : "Se connecter"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
